import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { useLayoutMaster } from '../utils/useLayoutMaster';
import { MagmaNewsSection } from '../components/MagmaNewsSection';
import { StocksHero } from '../components/StocksHero';

const DINO_URL = '/api/tickers61';
const B3_URL   = '/api/go';

// ── Currency-aware price formatter ──
const fmtPrice = (val, currency) => {
  if (val == null || isNaN(val)) return '—';
  const n = Number(val);
  const noDecimals = ['JPY','KRW','IDR','CLP','COP','HUF','TWD','VND'];
  const dp = noDecimals.includes(currency) ? 0 : 2;
  if (currency === 'BRL') return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
  if (currency === 'USD') return `$ ${n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
  if (currency === 'EUR') return `€ ${n.toFixed(dp)}`;
  if (currency === 'GBP') return `£ ${n.toFixed(dp)}`;
  return `${n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })} ${currency || ''}`;
};

const fmtVol = (v) => {
  if (!v) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};

// ── Exchange suffix → country flag ──
const EXCHANGE_FLAGS = {
  'SA': '🇧🇷', 'NS': '🇮🇳', 'BO': '🇮🇳', 'T':  '🇯🇵', 'L':  '🇬🇧',
  'DE': '🇩🇪', 'F':  '🇩🇪', 'PA': '🇫🇷', 'MI': '🇮🇹', 'MC': '🇪🇸',
  'AS': '🇳🇱', 'SW': '🇨🇭', 'TO': '🇨🇦', 'AX': '🇦🇺', 'NZ': '🇳🇿',
  'HK': '🇭🇰', 'SS': '🇨🇳', 'SZ': '🇨🇳', 'KS': '🇰🇷', 'KQ': '🇰🇷',
  'TW': '🇹🇼', 'BK': '🇹🇭', 'JK': '🇮🇩', 'KL': '🇲🇾', 'SI': '🇸🇬',
  'TA': '🇮🇱', 'AT': '🇬🇷', 'WA': '🇵🇱', 'PR': '🇨🇿', 'BR': '🇧🇷',
  'SN': '🇿🇦', 'ME': '🇷🇺',
};

const getFlag = (ticker) => {
  if (!ticker) return '🌐';
  const parts = ticker.split('.');
  if (parts.length < 2) return '🇺🇸';
  return EXCHANGE_FLAGS[parts[parts.length - 1]] || '🌐';
};

const getExchange = (ticker) => {
  if (!ticker) return 'US';
  const parts = ticker.split('.');
  if (parts.length < 2) return 'NYSE/NASDAQ';
  const sfx = parts[parts.length - 1];
  const map = {
    'SA': 'B3', 'NS': 'NSE', 'BO': 'BSE', 'T': 'TSE', 'L': 'LSE',
    'DE': 'XETRA', 'F': 'Frankfurt', 'PA': 'Euronext Paris', 'MI': 'Borsa Italiana',
    'MC': 'BME', 'AS': 'Euronext Amsterdam', 'SW': 'SIX', 'TO': 'TSX',
    'AX': 'ASX', 'NZ': 'NZX', 'HK': 'HKEX', 'SS': 'SSE', 'SZ': 'SZSE',
    'KS': 'KRX', 'KQ': 'KOSDAQ', 'TW': 'TWSE', 'BK': 'SET', 'JK': 'IDX',
    'KL': 'Bursa Malaysia', 'SI': 'SGX', 'TA': 'TASE', 'AT': 'ATHEX',
    'WA': 'GPW', 'SN': 'JSE', 'ME': 'MOEX',
  };
  return map[sfx] || sfx;
};

// ── Region buckets ──
const REGIONS = [
  { id: 'all',    label: 'Todos',           suffixes: null },
  { id: 'b3',     label: '🇧🇷 B3',          suffixes: ['SA'] },
  { id: 'us',     label: '🇺🇸 EUA',         suffixes: [''] },
  { id: 'americas', label: '🌎 Américas',   suffixes: ['TO'] },
  { id: 'europe', label: '🇪🇺 Europa',      suffixes: ['L','DE','F','PA','MI','MC','AS','SW','AT','WA','PR'] },
  { id: 'asia',   label: '🌏 Ásia/Pacífico',suffixes: ['T','HK','SS','SZ','KS','KQ','TW','NS','BO','BK','JK','KL','SI','AX','NZ'] },
  { id: 'other',  label: '🌍 Outros',       suffixes: ['TA','SN','ME'] },
];

const getSuffix = (ticker) => {
  const parts = (ticker || '').split('.');
  return parts.length < 2 ? '' : parts[parts.length - 1];
};

const getRegion = (ticker) => {
  const sfx = getSuffix(ticker);
  for (const r of REGIONS.slice(1)) {
    if (r.suffixes.includes(sfx)) return r.id;
  }
  return 'other';
};

// ── Mover chip ──
const MoverChip = ({ t, theme }) => {
  const up = t.var >= 0;
  return (
    <a href={`/ativo/${getSuffix(t.ticker) === 'SA' ? 'stocks' : 'world'}/${t.ticker}`}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: '7px 10px', borderRadius: 6, textDecoration: 'none',
        background: up ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
        border: `1px solid ${up ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13 }}>{getFlag(t.ticker)}</span>
        <span style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800, fontSize: 12, color: '#fff' }}>{t.ticker}</span>
      </div>
      <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 11,
        color: up ? '#4ade80' : '#f87171' }}>
        {up ? '+' : ''}{t.var?.toFixed(2)}%
      </span>
    </a>
  );
};

// ── Main table row ──
const TickerRow = ({ t, theme, idx }) => {
  const up = t.var >= 0;
  return (
    <tr style={{ borderBottom: `1px solid ${theme.border}`,
      background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
      transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(102,96,254,0.07)'}
      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}>
      <td style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>{getFlag(t.ticker)}</span>
          <div>
            <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800, fontSize: 13, color: '#fff' }}>{t.ticker}</div>
            <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 10, color: theme.textSecondary, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name || '—'}</div>
          </div>
        </div>
      </td>
      <td style={{ padding: '10px 8px', fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: '#a1a1aa', whiteSpace: 'nowrap' }}>
        {getExchange(t.ticker)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 13, color: '#fff', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {fmtPrice(t.ult, t.currency)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
        <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 12,
          color: up ? '#4ade80' : '#f87171',
          background: up ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
          padding: '2px 8px', borderRadius: 4, fontVariantNumeric: 'tabular-nums' }}>
          {up ? '+' : ''}{t.var?.toFixed(2)}%
        </span>
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: theme.textSecondary }}>
        {fmtVol(t.vol)}
      </td>
    </tr>
  );
};

// ── MAIN ──
export default function StocksPage() {
  const layout = useLayoutMaster();
  const [isDark, setIsDark] = useState(true);
  const [tickers, setTickers]   = useState([]);   // full universe from :6061/api/tickers
  const [movers, setMovers]     = useState(null);  // :6061/api/overview
  const [b3Data, setB3Data]     = useState(null);  // :8080/api/overview
  const [loading, setLoading]   = useState(true);
  const [region, setRegion]     = useState('all');
  const [search, setSearch]     = useState('');
  const [sortBy, setSortBy]     = useState('var'); // var | vol | name
  const [sortDir, setSortDir]   = useState('desc');
  const [showAll, setShowAll]   = useState(false); // table preview vs full list

  const PREVIEW_ROWS = 25;

  const theme = {
    background:    'rgba(10,10,10,0.95)',
    textPrimary:   '#fff',
    textSecondary: '#71717a',
    border:        'rgba(255,255,255,0.07)',
    cardBg:        'rgba(18,18,18,0.8)',
  };

  useEffect(() => {
    Promise.all([
      fetch(`${DINO_URL}/api/tickers`).then(r => r.json()).catch(() => []),
      fetch(`${DINO_URL}/api/overview`).then(r => r.json()).catch(() => null),
      fetch(`${B3_URL}/api/overview`).then(r => r.json()).catch(() => null),
    ]).then(([t, m, b]) => {
      setTickers(Array.isArray(t) ? t : (t?.tickers || []));
      setMovers(m);
      setB3Data(b);
      setLoading(false);
    });
  }, []);

  useEffect(() => { setShowAll(false); }, [region, search]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let list = tickers;
    if (region !== 'all') {
      const r = REGIONS.find(r => r.id === region);
      if (r) list = list.filter(t => r.suffixes.includes(getSuffix(t.ticker)));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t => t.ticker?.toLowerCase().includes(q) || t.name?.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      let av, bv;
      if (sortBy === 'var') { av = a.var ?? -999; bv = b.var ?? -999; }
      else if (sortBy === 'vol') { av = a.vol ?? 0; bv = b.vol ?? 0; }
      else { av = a.ticker || ''; bv = b.ticker || ''; return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [tickers, region, search, sortBy, sortDir]);

  const SortTh = ({ col, label, align = 'right' }) => (
    <th onClick={() => handleSort(col)}
      style={{ padding: '10px 12px', textAlign: align, color: sortBy === col ? '#6660fe' : theme.textSecondary,
        fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label} {sortBy === col ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  // Movers from both feeds
  const worldGainers = movers?.topGainers?.slice(0, 8) || [];
  const worldLosers  = movers?.topLosers?.slice(0, 8)  || [];
  const b3Gainers    = b3Data?.topGainers?.slice(0, 5) || [];
  const b3Losers     = b3Data?.topLosers?.slice(0, 5)  || [];

  return (
    <div style={{ minHeight: '100vh', background: '#080808', color: '#fff', position: 'relative' }}>

      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.3 }}>
        <Silk speed={20} scale={1.4} color="#6660fe" noiseIntensity={2} rotation={0.15} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header theme={theme} isDark={isDark} toggleTheme={() => setIsDark(!isDark)}
          topTickers={[...b3Gainers, ...worldGainers].slice(0, 10)} marketData={b3Data} />

        <main style={{ maxWidth: 1400, margin: '0 auto', padding: layout.isMobile ? '1rem' : '2rem' }}>

          {/* ── PINNED HERO: 52-week trophy + geopolitics news ── */}
          <StocksHero theme={theme} />

          {/* ── PAGE TITLE ── */}
          <div style={{ marginBottom: layout.isMobile ? '1.5rem' : '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <h1 style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800,
                fontSize: layout.isMobile ? '2rem' : '3rem',
                background: 'linear-gradient(to right,#6660fe,#a89bff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                margin: 0 }}>Mercado Global</h1>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700,
                padding: '3px 10px', borderRadius: 100,
                background: loading ? 'rgba(255,255,255,0.05)' : 'rgba(102,96,254,0.15)',
                color: loading ? '#71717a' : '#a89bff', border: '1px solid rgba(102,96,254,0.3)' }}>
                {loading ? 'CARREGANDO...' : `${tickers.length.toLocaleString()} ATIVOS · 31 BOLSAS`}
              </span>
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, color: '#71717a', fontSize: '0.9rem', margin: 0 }}>
              B3, NYSE, LSE, TSE, HKEX e mais 26 bolsas — cobertura global em tempo real.
            </p>
          </div>

          {/* ── MOVERS STRIP ── */}
          {!loading && (
            <div style={{ marginBottom: '2rem', display: 'grid', gridTemplateColumns: layout.isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div style={{ background: theme.cardBg, border: theme.border, borderRadius: 10, padding: '1rem' }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: '#4ade80', marginBottom: 8 }}>▲ ALTAS GLOBAIS</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 4 }}>
                  {worldGainers.map(t => <MoverChip key={t.ticker} t={t} theme={theme} />)}
                </div>
              </div>
              <div style={{ background: theme.cardBg, border: theme.border, borderRadius: 10, padding: '1rem' }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: '#f87171', marginBottom: 8 }}>▼ BAIXAS GLOBAIS</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 4 }}>
                  {worldLosers.map(t => <MoverChip key={t.ticker} t={t} theme={theme} />)}
                </div>
              </div>
            </div>
          )}

          {/* ── RR Site editorial feed — content-rich block up high ── */}
          <div style={{ marginBottom: '2.5rem' }}>
            <MagmaNewsSection label="Acervo Editorial" offset={0} />
          </div>

          {/* ── REGION TABS + SEARCH ── */}
          <div style={{ display: 'flex', flexDirection: layout.isMobile ? 'column' : 'row',
            gap: '0.75rem', alignItems: layout.isMobile ? 'stretch' : 'center', marginBottom: '1rem' }}>

            {/* Region tabs */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {REGIONS.map(r => (
                <button key={r.id} onClick={() => setRegion(r.id)}
                  style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 11,
                    padding: '5px 12px', borderRadius: 6, cursor: 'pointer', border: 'none',
                    background: region === r.id ? '#6660fe' : 'rgba(255,255,255,0.06)',
                    color: region === r.id ? '#fff' : '#71717a',
                    transition: 'all 0.15s' }}>
                  {r.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar ticker ou nome..."
              style={{ marginLeft: layout.isMobile ? 0 : 'auto',
                fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 12,
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 6, padding: '7px 12px', color: '#fff', outline: 'none', width: layout.isMobile ? '100%' : 220 }} />
          </div>

          {/* ── TABLE ── */}
          <div style={{ background: theme.cardBg, borderRadius: 10, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
            {/* Result count */}
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: '#71717a' }}>
                {filtered.length.toLocaleString()} ativos
              </span>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>
                DINO · {DINO_URL}
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.3)' }}>
                    <SortTh col="name" label="ATIVO" align="left" />
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: theme.textSecondary, fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em' }}>BOLSA</th>
                    <SortTh col="ult"  label="PREÇO" />
                    <SortTh col="var"  label="VAR %" />
                    <SortTh col="vol"  label="VOLUME" />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ padding: '3rem', textAlign: 'center', color: '#71717a', fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, letterSpacing: '0.1em' }}>CARREGANDO...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#71717a', fontFamily: "'IBM Plex Mono',monospace", fontSize: 11 }}>Nenhum ativo encontrado</td></tr>
                  ) : (
                    filtered.slice(0, showAll ? 200 : PREVIEW_ROWS).map((t, i) => <TickerRow key={t.ticker} t={t} theme={theme} idx={i} />)
                  )}
                </tbody>
              </table>
            </div>

            {!loading && filtered.length > PREVIEW_ROWS && (
              <div style={{ padding: '10px 14px', borderTop: `1px solid ${theme.border}`, textAlign: 'center' }}>
                <button onClick={() => setShowAll(s => !s)}
                  style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 11,
                    padding: '6px 16px', borderRadius: 6, cursor: 'pointer', border: '1px solid rgba(102,96,254,0.3)',
                    background: 'rgba(102,96,254,0.1)', color: '#a89bff' }}>
                  {showAll
                    ? `Recolher · mostrando ${Math.min(filtered.length, 200).toLocaleString()} de ${filtered.length.toLocaleString()}`
                    : `Ver todos os ${filtered.length.toLocaleString()} ativos ↓`}
                </button>
                {showAll && filtered.length > 200 && (
                  <div style={{ marginTop: 8, fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.1em' }}>
                    Limite de 200 — use a busca ou as regiões para refinar
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── RR Site editorial feed — continuation at the very bottom (offset → no repeats) ── */}
          <div style={{ marginTop: '2.5rem' }}>
            <MagmaNewsSection label="Acervo Editorial" offset={7} />
          </div>

        </main>
      </div>
    </div>
  );
}